import { useEffect } from "react";

type CssLoader = () => Promise<unknown>;

// Loads a partner's brand stylesheet independently of its theme component, so the
// CSS still applies for a partner that has no theme file. Non-eager, so each sheet
// stays its own lazy chunk fetched only when that partner renders; a theme file
// that also imports the same CSS resolves to the same module id, so Vite dedupes.
const themeCssLoaders: Record<string, CssLoader> = import.meta.glob(
  "./*/*_theme.css"
);

// Pure selector so the slug→loader mapping is unit-testable without the glob.
export function selectThemeCssLoader(
  loaders: Record<string, CssLoader>,
  slug?: string
): CssLoader | undefined {
  if (!slug) {
    return undefined;
  }
  return loaders[`./${slug}/${slug}_theme.css`];
}

// A brand that states a palette renders on the shared base instead of the partner's
// sheet. Loading both would mean the partner's rules — a button text colour pinned for
// its own primary, say — surviving underneath a brand they were never written for, and
// an imported sheet cannot be unloaded once Vite has injected it.
export function usePartnerThemeCss(slug?: string, skip = false) {
  useEffect(() => {
    if (skip) {
      return;
    }
    // A failed CSS chunk leaves the page unstyled but functional; swallow the
    // rejection so it doesn't surface as an unhandled promise error.
    selectThemeCssLoader(themeCssLoaders, slug)?.()?.catch(() => undefined);
  }, [slug, skip]);
}
